import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Bug, Shield, Phone, Check, MapPin, ChevronRight, Home, AlertTriangle, FileSearch, Syringe, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Header } from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// SEO Component
const PageSEO = () => (
  <>
    <title>Termite Inspection Ripley | Barriers & Treatment | Pest Free</title>
    <meta name="description" content="Professional termite inspections and barriers in Ripley, QLD. Protect your new build from termites. QBCC Licensed. Serving Ripley, Providence & Springfield." />
    <meta name="keywords" content="termite inspection Ripley, termite barrier Ripley QLD, white ant control Ripley, termite treatment Providence, pre-construction termite Ripley, termite inspection Springfield" />
    <link rel="canonical" href="https://pestfreebrisbane.com.au/termite-inspection-ripley" />
    <meta property="og:title" content="Termite Inspections & Barriers Ripley | Pest Free Ipswich & Brisbane" />
    <meta property="og:description" content="Professional termite protection for Ripley homes. Inspections, barriers, and ongoing management. Call (07) 3708 1422." />
    <meta property="og:url" content="https://pestfreebrisbane.com.au/termite-inspection-ripley" />
  </>
);

// Theme colors for termites - Brown/Orange
const theme = {
  primary: "#D2691E",
  primaryDark: "#B8581A",
  primaryLight: "#D2691E20",
  gradient: "from-[#D2691E] to-[#8B4513]"
};

export default function TermiteInspectionRipleyPage() {
  const contactRef = useRef(null);
  
  const scrollToContact = () => {
    contactRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const services = [
    { 
      icon: FileSearch, 
      title: "Termite Inspections", 
      desc: "Thorough inspections using the latest technology to detect termite activity and damage." 
    },
    { 
      icon: Syringe, 
      title: "Active Treatments", 
      desc: "Direct treatment of active termite infestations to eliminate colonies fast." 
    },
    { 
      icon: Shield, 
      title: "Chemical Barriers", 
      desc: "Professional soil treatment creating a protective barrier around your property." 
    },
    { 
      icon: Home, 
      title: "Baiting Systems", 
      desc: "Strategic bait stations to eliminate entire termite colonies." 
    },
    { 
      icon: Clock, 
      title: "Management Plans", 
      desc: "Ongoing protection programs tailored to your property's needs." 
    }
  ];

  const warningSignsList = [
    "Hollow-sounding timber when tapped",
    "Mud tubes on walls or foundations",
    "Bubbling or cracked paint",
    "Tight-fitting doors or windows",
    "Discarded termite wings",
    "Visible termite droppings (frass)"
  ];

  const ripleyAreas = [
    "Ripley Valley",
    "Providence",
    "Ecco Ripley", 
    "South Ripley",
    "Deebing Heights",
    "Springfield",
    "Redbank Plains"
  ];

  return (
    <div className="min-h-screen">
      <PageSEO />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden" data-testid="ripley-termite-hero">
        <div className={`absolute inset-0 bg-gradient-to-br ${theme.gradient}`} />
        <div className="absolute inset-0 bg-slate-900/20" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <MapPin className="w-5 h-5 text-white" />
              <span className="font-medium text-white">Servicing Ripley & Surrounds</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-6 leading-tight text-white">
              Termite Inspections & Termite Barriers <span className="text-yellow-300">Ripley</span>
            </h1>
            
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Protect your biggest investment. Professional termite inspections, chemical barriers, and management systems for Ripley's new builds and established homes.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={scrollToContact}
                className="bg-white text-[#D2691E] hover:bg-slate-100 text-lg px-8 py-6 rounded-full font-semibold shadow-lg flex items-center justify-center gap-2"
              >
                <FileSearch className="w-5 h-5" />
                Book an Inspection
              </Button>
              <a 
                href="tel:0737081422"
                className="inline-flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 text-white border-2 border-white px-8 py-4 rounded-full text-lg font-semibold transition-all"
              >
                <Phone className="w-5 h-5" />
                (07) 3708 1422
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Ripley-Specific Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-6">
                Termite Protection for <span style={{ color: theme.primary }}>Ripley's New Estates</span>
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Ripley's rapid development makes it a high-risk area for termite activity. Construction disrupts existing termite colonies, and new builds without proper protection become easy targets.
              </p>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Whether you've recently built in Providence, Ecco Ripley, or South Ripley, or you're in an established property near Springfield or Redbank Plains, regular termite inspections are essential. Termites cause over $1.5 billion in damage to Australian homes each year — don't let your home become a statistic.
              </p>
              <Link 
                to="/termites"
                className="inline-flex items-center gap-2 text-[#D2691E] font-semibold hover:underline"
              >
                View our full Termite Services <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="bg-red-50 rounded-2xl p-8 border border-red-200">
              <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#D2691E]" />
                Why Ripley is High-Risk
              </h3>
              <ul className="space-y-3">
                {[
                  "New construction disturbs established termite colonies",
                  "Surrounding bushland provides constant termite pressure",
                  "Many new builds lack adequate termite barriers",
                  "Development removes natural predators",
                  "Moisture from construction attracts termites",
                  "Landscaping and mulch create ideal conditions"
                ].map((issue, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#D2691E] mt-0.5 shrink-0" />
                    <span className="text-slate-600">{issue}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
              Our Termite <span style={{ color: theme.primary }}>Services</span>
            </h2>
            <p className="text-lg text-slate-600">Comprehensive termite management from inspection to ongoing protection</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {services.map((service, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-slate-100 hover:border-[#D2691E]/30 hover:shadow-lg transition-all">
                <div className="inline-flex p-3 rounded-xl mb-4" style={{ backgroundColor: theme.primaryLight }}>
                  <service.icon className="w-8 h-8" style={{ color: theme.primary }} />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{service.title}</h3>
                <p className="text-slate-600">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Warning Signs */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Warning Signs of <span className="text-[#D2691E]">Termite Damage</span>
            </h2>
            <p className="text-lg text-slate-400">Don't ignore these signs — call us immediately</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {warningSignsList.map((sign, i) => (
              <div key={i} className="flex items-start gap-4 bg-white/5 rounded-xl p-5 border border-white/10">
                <div className="p-2 bg-[#D2691E]/20 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-[#D2691E]" />
                </div>
                <p className="text-slate-200 font-medium">{sign}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <p className="text-slate-400 mb-4">Termites cause over $1.5 billion in damage to Australian homes each year.</p>
            <Button 
              onClick={scrollToContact}
              className="bg-[#D2691E] hover:bg-[#B8581A] text-white px-8 py-4 rounded-full text-lg font-semibold"
            >
              Book an Inspection in Ripley
            </Button>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
              Termite Services Across <span style={{ color: theme.primary }}>Ripley & Surrounds</span>
            </h2>
          </div>
          
          <div className="flex flex-wrap justify-center gap-3">
            {ripleyAreas.map((area, i) => (
              <span key={i} className="px-4 py-2 bg-slate-100 rounded-full text-slate-700 font-medium">
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section ref={contactRef} className="py-16 bg-slate-50" data-testid="ripley-termite-contact">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
              Book a Termite Inspection in <span style={{ color: theme.primary }}>Ripley</span>
            </h2>
            <p className="text-lg text-slate-600">
              Protect your property. We'll respond within 2 hours during business hours.
            </p>
          </div>
          
          <ContactForm />
        </div>
      </section>

      {/* Back to Main Page CTA */}
      <section className="py-12 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-600 mb-4">Looking for more information about our termite services?</p>
          <Link 
            to="/termites"
            className="inline-flex items-center gap-2 text-[#D2691E] font-semibold hover:underline text-lg"
          >
            Visit our main Termite Services page <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}

// Contact Form Component
function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    suburb: "Ripley",
    propertyType: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        serviceType: "termite-inspection-ripley",
        timestamp: new Date().toISOString(),
        pageUrl: window.location.href
      });
      setSubmitted(true);
      toast.success("Inspection request sent! We'll contact you within 2 hours.");
    } catch (error) {
      toast.error("Failed to send request. Please call us on (07) 3708 1422.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl p-8 border border-slate-200 text-center">
        <div className="w-16 h-16 bg-[#7ABC32]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-[#7ABC32]" />
        </div>
        <h3 className="text-2xl font-semibold text-slate-900 mb-2">Thanks for your enquiry!</h3>
        <p className="text-slate-600 mb-6">We'll be in touch within 2 hours during business hours.</p>
        <a href="tel:0737081422" className="inline-flex items-center gap-2 text-[#D2691E] font-semibold">
          <Phone className="w-4 h-4" /> Or call us now: (07) 3708 1422
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 border border-slate-200 space-y-6">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name">Full Name *</Label>
          <Input
            id="name"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Your name"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="phone">Phone *</Label>
          <Input
            id="phone"
            type="tel"
            required
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="0400 000 000"
            className="mt-1"
          />
        </div>
      </div>
      
      <div>
        <Label htmlFor="email">Email *</Label>
        <Input
          id="email"
          type="email"
          required
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="your@email.com"
          className="mt-1"
        />
      </div>
      
      <div>
        <Label htmlFor="address">Property Address</Label>
        <Input
          id="address"
          value={formData.address}
          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          placeholder="Street address (optional)"
          className="mt-1"
        />
      </div>
      
      <div>
        <Label htmlFor="suburb">Suburb</Label>
        <Input
          id="suburb"
          value={formData.suburb}
          onChange={(e) => setFormData({ ...formData, suburb: e.target.value })}
          placeholder="e.g., Ripley, Providence, Springfield"
          className="mt-1"
        />
      </div>
      
      <div>
        <Label htmlFor="message">Additional Details</Label>
        <Textarea
          id="message"
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          placeholder="Any concerns or details about your property?"
          rows={4}
          className="mt-1"
        />
      </div>
      
      <Button 
        type="submit" 
        disabled={submitting}
        className="w-full bg-[#D2691E] hover:bg-[#B8581A] text-white py-4 rounded-full text-lg font-semibold"
      >
        {submitting ? "Sending..." : "Book Inspection"}
      </Button>
      
      <p className="text-center text-sm text-slate-500">
        Or call us directly: <a href="tel:0737081422" className="text-[#D2691E] font-medium">(07) 3708 1422</a>
      </p>
    </form>
  );
}
